import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import {
    WarningOutlined,
    WarningFilled,
    TabletOutlined,
    TabletFilled,
} from "@ant-design/icons";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { hasPermission } from 'utils/utils';
import MobileAppModal from './MobileAppModal';
import { fetchSubscriptions } from 'store/slices/subscriptionsSlice';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { OpNav } from 'components/customAntd/inVision - Platinum Assets/OpNav/OpNav';
import { HomeOutlinedSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/HomeOutlinedSvg/HomeOutlinedSvg';
import { HomeFilledSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/HomeFilledSvg/HomeFilledSvg';
import { UserGroupFilledSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/UserGroupFilledSvg/UserGroupFilledSvg';
import { UserGroupOutlineSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/UserGroupOutlineSvg/UserGroupOutlineSvg';
import { LocationFilledSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/LocationFilledSvg/LocationFilledSvg';
import { LocationOutlinedSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/LocationOutlinedSvg/LocationOutlinedSvg';
import { SettingFilledSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/SettingFilledSvg/SettingFilledSvg';
import { SettingOutlinedSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/SettingOutlinedSvg/SettingOutlinedSvg';
import { AdminFilledSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/AdminFilledSvg/AdminFilledSvg';
import { AdminOutlinedSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/AdminOutlinedSvg/AdminOutlinedSvg';
import { ReportSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/ReportSvg';
import { DevicesOutlinedSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/DevicesOutlinedSvg/DevicesOutlinedSvg';
import ActionFloatButtons from '../pageContent/ActionFloatButtons/ActionFloatButtons';
import { ReportFilledSvg } from 'components/customAntd/inVision - Platinum Assets/svgs/ReportSvg/ReportFilledSvg';
import { useTranslation } from 'react-i18next';
import { ReactComponent as VisitorLogo } from 'components/customAntd/inVision - Platinum Assets/svgs/InvisitSvg/Visitor.svg';
import { fetchIntegrationStatus } from 'store/slices/integrationSlice';

interface SideMenuProps {
    onClose: () => void;
    isMobile: boolean;
}

const SideMenu: React.FC<SideMenuProps> = ({ onClose, isMobile }) => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

    const location = useLocation();
    const [mobileAppModalOpen, setMobileAppModalOpen] = useState(false);
    const [featureUnavailableModalOpen, setFeatureUnavailableModalOpen] = useState(false);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const subscriptions = useSelector((state: RootState) => state.subscriptions.subscriptions);
    const { isMsiIntegrated, isGuestPassIntegrated, isTokenValid } = useSelector(
        (state: RootState) => state.integration
    );

    const hasAdminRead = hasPermission(tokenScopeList, orgId, 'o', 'admin:r');
    const hasVisitorsRead = hasPermission(tokenScopeList, orgId, 'o', 'visitors:r');
    const hasDashRead = hasPermission(tokenScopeList, orgId, 'o', 'dash:r');
    const hasLocationRead = hasPermission(tokenScopeList, orgId, 'o', 'location:r');
    const hasUsersRead = hasPermission(tokenScopeList, orgId, 'o', 'user:r');
    const hasRolesRead = hasPermission(tokenScopeList, orgId, 'o', 'role:r');
    const hasKiosksRead = hasPermission(tokenScopeList, orgId, 'o', 'kiosk:r');
    const hasRedFlagConfRead = hasPermission(tokenScopeList, orgId, 'o', 'redflagconf:r');
    const hasInvitationRead = hasPermission(tokenScopeList, orgId, 'o', 'invitation:r');
    const hasSigninconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:r');
    const hasSignoutconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signoutconf:r');
    const hasFieldsRead = hasPermission(tokenScopeList, orgId, 'o', 'fields:r');
    const hasAgreementsRead = hasPermission(tokenScopeList, orgId, 'o', 'agreements:r');
    const hasEmailTemplatesRead = hasPermission(tokenScopeList, orgId, 'o', 'emailtemplates:r');
    const hasAccountRead = hasPermission(tokenScopeList, orgId, 'o', 'account:r');
    const hasIntegrationRead = hasPermission(tokenScopeList, orgId, 'o', 'integration:r');
    const hasRptVisitorRead = hasPermission(tokenScopeList, orgId, 'o', 'rptvisitor:r');
    const hasRptTrendRead = hasPermission(tokenScopeList, orgId, 'o', 'rpttrend:r');
    const hasRptRedFlagRead = hasPermission(tokenScopeList, orgId, 'o', 'rptredflag:r');
    const hasRptHostRead = hasPermission(tokenScopeList, orgId, 'o', 'rpthost:r');
    const hasRptActivityRead = hasPermission(tokenScopeList, orgId, 'o', 'rptactivity:r');
    const hasRptGuestPassRead = hasPermission(tokenScopeList, orgId, 'o', 'rptguestpass:r');

    const showUsers = hasUsersRead;
    const showRoles = hasRolesRead;
    const showKiosks = hasKiosksRead;
    const showRedFlag = hasRedFlagConfRead;
    const showInvitations = hasInvitationRead;
    const showWorkflow = hasSigninconfRead || hasSignoutconfRead;
    const showFields = hasFieldsRead;
    const showAgreements = hasAgreementsRead;
    const showEmailTemplates = hasEmailTemplatesRead;
    const showAccount = hasAccountRead;
    const showIntegration = hasIntegrationRead;
    const showRptVisitor = hasRptVisitorRead;
    const showRptHourlyVisitors = hasRptVisitorRead;
    const showRptTrend = hasRptTrendRead;
    const showRptRedFlag = hasRptRedFlagRead;
    const showRptHost = hasRptHostRead;
    const showRptActivity = hasRptActivityRead;
    const showRptGuestPass = hasRptGuestPassRead && isGuestPassIntegrated && isTokenValid;
    const showAvigilonReaders = hasSigninconfRead && isMsiIntegrated && isTokenValid;

    const { t } = useTranslation();

    useEffect(() => {
        if (orgId) {
            dispatch(fetchIntegrationStatus({ orgId }));
        }

        if (!subscriptions || subscriptions.data.length === 0) {
            dispatch(fetchSubscriptions({ orgId }));
        }
    }, [dispatch, orgId, subscriptions]);


    const items = [
        ...(hasDashRead
            ? [
                {
                    key: "/dashboard",
                    icon: location.pathname === "/dashboard" ? (
                        <HomeFilledSvg />
                    ) : (
                        <HomeOutlinedSvg />
                    ),
                    label: t('sideMenu.home'),
                    onClick: () => navigate("/dashboard"),
                },
            ]
            : []),
        ...(hasVisitorsRead
            ? [
                {
                    key: "/visitors",
                    icon: location.pathname === "/visitors" ? (
                        <UserGroupFilledSvg />
                    ) : (
                        <UserGroupOutlineSvg />
                    ),
                    label: t('sideMenu.visitors'),
                    onClick: () => navigate("/visitors"),
                },
            ]
            : []),
        ...(hasLocationRead
            ? [
                {
                    key: "/locations",
                    icon: location.pathname === "/locations" ? (
                        <LocationFilledSvg />
                    ) : (
                        <LocationOutlinedSvg />
                    ),
                    label: t('sideMenu.locations'),
                    onClick: () => navigate("/locations"),
                },
            ]
            : []),
        ...(showKiosks
            ? [
                {
                    key: "/kiosks",
                    icon: location.pathname === "/kiosks" ? (
                        <TabletFilled style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                    ) : (
                        <TabletOutlined style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                    ),
                    label: t('sideMenu.kiosks'),
                    onClick: () => navigate("/kiosks"),
                },
            ]
            : []),
        ...(showRedFlag
            ? [
                {
                    key: "/redFlag",
                    icon: location.pathname === "/redFlag" ? (
                        <WarningFilled style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                    ) : (
                        <WarningOutlined style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                    ), label: t('sideMenu.redFlag'),
                    onClick: () => navigate("/redFlag"),
                },
            ]
            : []),
        ...(showRptVisitor || showRptHourlyVisitors || showRptTrend || showRptRedFlag || showRptHost || showRptActivity || showRptGuestPass
            ? [
                {
                    key: "/reports",
                    icon: location.pathname.startsWith("/reports") ? (
                        <ReportFilledSvg />
                    ) : (
                        <ReportSvg />
                    ),
                    label: t('sideMenu.reports.title'),
                    children: [
                        ...(showRptVisitor
                            ? [{ key: "/reports/visitorReport", label: t('sideMenu.reports.visitorReport'), onClick: () => navigate("/reports/visitorReport") }]
                            : []),
                        ...(showRptHourlyVisitors
                            ? [{ key: "/reports/visitorsHourlyReport", label: t('sideMenu.reports.visitorsByHour'), onClick: () => navigate("/reports/visitorsHourlyReport") }]
                            : []),
                        ...(showRptTrend
                            ? [{ key: "/reports/repeatVisitors", label: t('sideMenu.reports.repeatVisitors'), onClick: () => navigate("/reports/repeatVisitors") }]
                            : []),
                        ...(showRptRedFlag
                            ? [{ key: "/reports/redFlagReport", label: t('sideMenu.reports.redFlagReport'), onClick: () => navigate("/reports/redFlagReport") }]
                            : []),
                        ...(showRptGuestPass
                            ? [{ key: "/reports/guestPassReport", label: t('sideMenu.reports.guestPass'), onClick: () => navigate("/reports/guestPassReport") }]
                            : []),
                        ...(showRptGuestPass
                            ? [{ key: "/reports/guestPassLog", label: t('sideMenu.reports.guestPassLog'), onClick: () => navigate("/reports/guestPassLog") }]
                            : []),
                        ...(showRptHost
                            ? [{ key: "/reports/hostReport", label: t('sideMenu.reports.hostReport'), onClick: () => navigate("/reports/hostReport") }]
                            : []),
                        ...(showRptActivity
                            ? [{ key: "/reports/activityLog", label: t('sideMenu.reports.activityLog'), onClick: () => navigate("/reports/activityLog") }]
                            : []),
                    ],
                },
            ]
            : []),
        ...(showInvitations || showWorkflow || showFields || showAgreements || showUsers || showRoles || showEmailTemplates || showAvigilonReaders
            ? [
                {
                    key: "/configuration",
                    icon: location.pathname.startsWith("/configuration") ? (
                        <SettingFilledSvg />
                    ) : (
                        <SettingOutlinedSvg />
                    ),
                    label: t('sideMenu.configuration.title'),
                    children: [
                        ...(showInvitations
                            ? [{ key: "/configuration/invitations", label: t('sideMenu.configuration.invitations'), onClick: () => navigate("/configuration/invitations") }]
                            : []),
                        ...(showWorkflow
                            ? [{ key: "/configuration/workflow", label: t('sideMenu.configuration.workflow'), onClick: () => navigate("/configuration/workflow") }]
                            : []),
                        ...(showFields
                            ? [{ key: "/configuration/formFields", label: t('sideMenu.configuration.formFields'), onClick: () => navigate("/configuration/formFields") }]
                            : []),
                        ...(showAgreements
                            ? [{ key: "/configuration/agreements", label: t('sideMenu.configuration.agreements'), onClick: () => navigate("/configuration/agreements") }]
                            : []),
                        ...(showUsers
                            ? [{ key: "/configuration/users", label: t('sideMenu.configuration.users'), onClick: () => navigate("/configuration/users") }]
                            : []),
                        ...(showRoles
                            ? [{ key: "/configuration/userRoles", label: t('sideMenu.configuration.roles'), onClick: () => navigate("/configuration/userRoles") }]
                            : []),
                        ...(showEmailTemplates
                            ? [{ key: "/configuration/emailTemplates", label: t('sideMenu.configuration.emailTemplates'), onClick: () => navigate("/configuration/emailTemplates") },]
                            : []),
                        ...(showAvigilonReaders
                            ? [{ key: "/configuration/avigilonReaders", label: t('sideMenu.configuration.avigilonReaders'), onClick: () => navigate("/configuration/avigilonReaders") }]
                            : []),
                    ],
                },
            ]
            : []),
        ...(hasAdminRead && (showAccount || showIntegration)
            ? [
                {
                    key: "/administration",
                    icon: location.pathname.startsWith("/administration") ? (
                        <AdminFilledSvg />
                    ) : (
                        <AdminOutlinedSvg />
                    ),
                    label: t('sideMenu.administration.title'),
                    children: [
                        ...(showAccount
                            ? [{ key: "/administration/account", label: t('sideMenu.administration.account'), onClick: () => navigate("/administration/account") }]
                            : []),
                        ...(showIntegration
                            ? [{ key: "/administration/integration", label: t('sideMenu.administration.integration'), onClick: () => navigate("/administration/integration") }]
                            : []),
                    ],
                },
            ]
            : []),
        {
            key: "mobileApp",
            icon: <DevicesOutlinedSvg />,
            label: t('sideMenu.mobile'),
            onClick: () => setMobileAppModalOpen(true),
        },
    ];

    return (
        <>
            <OpNav
                logo={<VisitorLogo
                    style={{
                        color: isDarkMode ? 'white' : 'black',
                    }} />
                }
                items={items}
                onLogoClick={() => {
                    const navOrder = [
                        { path: '/dashboard', condition: hasDashRead },
                        { path: '/visitors', condition: hasVisitorsRead },
                        { path: '/locations', condition: hasLocationRead },
                        { path: '/kiosks', condition: showKiosks },
                        { path: '/redFlag', condition: showRedFlag },
                        {
                            condition: showRptVisitor || showRptHourlyVisitors || showRptTrend || showRptRedFlag || showRptHost || showRptActivity || showRptGuestPass,
                            path: (() => {
                                if (showRptVisitor) return '/reports/visitorReport';
                                if (showRptHourlyVisitors) return '/reports/visitorsHourlyReport';
                                if (showRptTrend) return '/reports/repeatVisitors';
                                if (showRptRedFlag) return '/reports/redFlagReport';
                                if (showRptHost) return '/reports/hostReport';
                                if (showRptActivity) return '/reports/activityLog';
                                if (showRptGuestPass) return '/reports/guestPassReport';
                                if (showRptGuestPass) return '/reports/guestPassLog';
                            })(),
                        },
                        {
                            condition: showInvitations || showWorkflow || showFields || showAgreements || showUsers || showRoles || showEmailTemplates || showAvigilonReaders,
                            path: (() => {
                                if (showInvitations) return '/configuration/invitations';
                                if (showWorkflow) return '/configuration/workflow';
                                if (showFields) return '/configuration/formFields';
                                if (showAgreements) return '/configuration/agreements';
                                if (showUsers) return '/configuration/users';
                                if (showRoles) return '/configuration/userRoles';
                                if (showEmailTemplates) return '/configuration/emailTemplates';
                                if (showAvigilonReaders) return '/configuration/avigilonReaders';

                            })(),
                        },
                        {
                            condition: hasAdminRead && (showAccount || showIntegration),
                            path: (() => {
                                if (showAccount) return '/administration/account';
                                if (showIntegration) return '/administration/integration';
                            })(),
                        },
                    ];
                    const firstAccessible = navOrder.find(item => item.condition && item.path);
                    navigate(firstAccessible?.path || '/support');
                }}
                defaultPrimaryKey={location.pathname.split('/')[1] ? `/${location.pathname.split('/')[1]}` : "/dashboard"}
                defaultSecondaryKey={location.pathname}
                isMobile={isMobile}
                onClose={onClose}
            />

            <div style={{ position: 'fixed', zIndex: 1100 }}>
                <ActionFloatButtons />
            </div>

            <MobileAppModal
                open={mobileAppModalOpen}
                onClose={() => setMobileAppModalOpen(false)}
            />

            <OpModal
                open={featureUnavailableModalOpen}
                onCancel={() => setFeatureUnavailableModalOpen(false)}
                onOk={() => setFeatureUnavailableModalOpen(false)}
                title={t('featureUnavailable.title')}
            >
                <p>{t('featureUnavailable.description')}</p>
            </OpModal>
        </>
    );
};

export default SideMenu;